import { useCallback, useEffect, useRef, useState } from "react";
import { Button, Modal } from "@legal-500/legal500-design-system";
import { companies as companiesService, type Company } from "../../../api/addressBook.service.js";
import { awards as awardsService } from "../../../api/awards.service.js";
import styles from "./LinkFirmModal.module.css";

/**
 * Both copied from components/CompanyPicker, and neither is cosmetic.
 *
 * The upstream search is an unindexed `ILIKE '%term%'` over the whole companies
 * table (address-book-api/src/models/companies.ts:126), so one keystroke is one
 * sequential scan. LinkContactModal fires per keystroke against a different,
 * cheaper endpoint; doing that here would put a scan per character on a table
 * this app cannot afford to make slow for everyone else.
 *
 * The cap is what makes the truncation line below possible: 20 rows is more than
 * anyone reads and `pagination.total` still reports the real size, so a term that
 * matches half the Address Book says so instead of silently showing its first 20.
 */
const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_LIMIT = 20;

type LinkFirmModalProps = {
  awardId: string;
  /** Verbatim firm text from the source spreadsheet. Seeds the search *and*
   * stays on screen in its own right — see the header line below. */
  firmNameRaw: string;
  onClose: () => void;
  onLinked: () => void;
};

/**
 * Links an award the importer could not match to an Address Book company.
 *
 * Shape, and most of the mechanics, from
 * pages/Research/ContentInbox/components/LinkContactModal.tsx — the same job
 * already working in this app: search, a table of candidates, one "Link" button
 * per row, failures reported inside the modal. The debounce and the stale-response
 * guard come from components/CompanyPicker instead, for the reason above.
 *
 * No toast on success: staff-ui has no toast mechanism at all, and the list
 * refetching underneath with the resolved firm name in place of the raw text is
 * the confirmation.
 */
export function LinkFirmModal({ awardId, firmNameRaw, onClose, onLinked }: LinkFirmModalProps) {
  const [searchTerm, setSearchTerm] = useState(firmNameRaw);
  const [results, setResults] = useState<Company[]>([]);
  const [totalMatches, setTotalMatches] = useState(0);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [linkingId, setLinkingId] = useState<string | null>(null);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestIdRef = useRef(0);
  const initialSearchDone = useRef(false);

  const search = useCallback(async (term: string) => {
    const trimmed = term.trim();

    // An empty box is not a search for everything: `name=` would be dropped by
    // the service and the request would return the first 20 companies in the
    // Address Book, which have nothing to do with this award.
    if (trimmed === "") {
      // Still take a request id, so a response already in flight for the
      // previous term cannot land after the box was emptied.
      requestIdRef.current += 1;
      setResults([]);
      setTotalMatches(0);
      setSearching(false);
      setError(null);
      return;
    }

    const requestId = ++requestIdRef.current;
    setSearching(true);
    setError(null);

    try {
      const response = await companiesService.getAll({ search: trimmed, limit: SEARCH_LIMIT });
      // A slower earlier request must not overwrite a faster later one: the box
      // is edited freely and responses arrive in whatever order the server
      // manages, so only the newest request is allowed to write state.
      if (requestId !== requestIdRef.current) return;
      setResults(response.data);
      setTotalMatches(response.pagination.total);
    } catch {
      if (requestId !== requestIdRef.current) return;
      setResults([]);
      setTotalMatches(0);
      setError("Couldn't search the Address Book. Try again.");
    } finally {
      if (requestId === requestIdRef.current) setSearching(false);
    }
  }, []);

  // One search on open, undebounced: the seeded term is already on screen, so
  // making the user wait 300ms to see what it matches would be waiting for
  // nothing. The ref guard is LinkContactModal's — StrictMode double-invokes
  // effects in development and this must stay one request.
  useEffect(() => {
    if (initialSearchDone.current) return;
    initialSearchDone.current = true;
    search(firmNameRaw);
  }, []);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  function handleSearchChange(next: string) {
    setSearchTerm(next);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      search(next);
    }, SEARCH_DEBOUNCE_MS);
  }

  async function handleLink(company: Company) {
    setLinkingId(company.id);
    setError(null);

    try {
      // `company.id`, the Address Book uuid — NOT `company.companyId`, which is
      // the legacy integer the column beside the name shows. The two are both
      // called "id" in conversation and only one of them is what the award row
      // stores.
      await awardsService.match(awardId, { companyId: company.id });
      onLinked();
    } catch {
      setError("Couldn't link this company. Try again.");
      setLinkingId(null);
    }
  }

  const isLinking = linkingId !== null;
  const hasSearchTerm = searchTerm.trim() !== "";
  const isTruncated = totalMatches > results.length;

  return (
    <Modal isOpen={true} onClose={onClose} title="Link Firm" size="medium" blurBackground>
      <div className={styles.body}>
        {/* The search box below is seeded with this text and the user edits it
            away to find anything, so this line is the only place the spreadsheet's
            own words stay readable while they search. Secondary styling, because
            it is context for the search rather than a thing to act on. */}
        <p className={styles.sourceLine}>
          Spreadsheet says: <span className={styles.sourceText}>“{firmNameRaw}”</span>
        </p>

        <input
          className={styles.searchInput}
          type="text"
          value={searchTerm}
          onChange={event => handleSearchChange(event.target.value)}
          placeholder="Search Address Book companies by name..."
          aria-label="Company name"
        />

        {error && <p className={styles.error}>{error}</p>}

        <div className={styles.results}>
          {searching && <p className={styles.hint}>Searching...</p>}
          {!searching && !hasSearchTerm && (
            <p className={styles.hint}>Type part of a firm name to search.</p>
          )}
          {/* Deliberately not phrased as a failure. The importer already tried
              this exact name, and tried it hard — apostrophe, diacritic,
              legal-suffix and trailing-token variants (awards-api's
              awards-matcher.ts) — which is precisely why this row is on screen.
              Nothing found here is the expected outcome of a search for a name
              the Address Book spells differently, and the only useful next move
              is a shorter term. */}
          {!searching && hasSearchTerm && results.length === 0 && (
            <p className={styles.hint}>No companies found. Try a shorter part of the name.</p>
          )}
          {!searching && results.length > 0 && (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Company ID</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {results.map(company => (
                  <tr key={company.id}>
                    <td>{company.name}</td>
                    {/* The legacy integer id, which is what tells two firms of
                        the same name apart — the one thing a name column cannot
                        do on its own. */}
                    <td>{company.companyId}</td>
                    <td>
                      <Button
                        variant="outlined"
                        color="default"
                        size="small"
                        disabled={isLinking}
                        onClick={() => handleLink(company)}
                      >
                        {linkingId === company.id ? "Linking..." : "Link"}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {!searching && isTruncated && (
            <p className={styles.hint}>
              Showing the first {results.length} of {totalMatches} matches — narrow the search to
              see the rest.
            </p>
          )}
        </div>

        <div className={styles.footer}>
          <Button variant="outlined" color="default" onClick={onClose} disabled={isLinking}>
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
}
